// ══════════════════════════════════════════════════════════════════════════════════════════════
// LE DROIT D'ENVOYER — suspendre un canal pour un magasin, et le lui rendre (cadrage §11.8).
//
// POURQUOI CETTE CARTE EXISTE, ET POURQUOI ICI. C'est une décision d'ÉDITEUR sur un client — un
// impayé, un compte suspendu, un usage abusif — donc elle se pose en Console et nulle part ailleurs.
// Rangée dans le back-office, elle serait levable par celui qu'elle vise.
//
// ⚠️ 🔴 DEUX ÉTATS SEULEMENT, ET C'EST UNE DÉCISION. Le cadrage décrit un compteur à trois états
// (`NULL` illimité · `0` bloqué · `1..N` plafond décompté), mais un plafond chiffré ne veut rien dire
// tant que sa FENÊTRE n'est pas tranchée — « 500 messages » par mois civil, en enveloppe unique ou
// sur 30 jours glissants sont trois produits différents. Offrir ici un nombre que rien n'applique
// serait une *option offerte non implémentée*, le quatrième interdit de §23.0. L'écran le DIT plutôt
// que de le taire.
//
// ⚠️ LE MOTIF EST OBLIGATOIRE POUR SUSPENDRE : c'est lui que le commerçant lira à la place de son
// bouton d'envoi. Un refus muet l'envoie appeler le support pour apprendre ce que l'écran pouvait
// dire — et il se saisit AVANT la suspension, pour qu'il n'existe aucune fenêtre, même courte, où le
// commerçant lit un refus sans raison.
//
// ⚠️ LE MOTIF SE SAISIT EN LIGNE, JAMAIS PAR `window.prompt` : les dialogues natifs sont proscrits
// dans toutes les apps du dépôt, et la carte voisine (compte SMS) ouvre déjà ses formulaires ainsi.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { alert, confirm, showToast, useLatest } from '@orgazone/ui-web';
import { api, type BoChannelAllowance } from '../api';

/**
 * Le nom lisible d'un canal — l'écran ne montre jamais la clé technique.
 *
 * ⚠️ LES MOTS SONT CEUX QUE LE COMMERÇANT VOIT (`campaign-channels.ts` du back-office) : le staff qui
 * suspend « Wallet » et le magasin qui lit « Wallet » doivent employer le même vocabulaire, sinon un
 * échange avec le support porte sur deux noms pour un seul canal. ⚠️ La table n'est pas PARTAGÉE pour
 * autant : celle du back-office se traduit, celle-ci vit sur une surface éditeur qui reste en
 * français — mettre un moteur de traduction en commun pour trois mots coûterait plus que la règle
 * qu'il ferait respecter.
 */
const CHANNEL_LABELS: Record<BoChannelAllowance['channel'], string> = {
  email: 'E-mail',
  sms: 'SMS',
  wallet_push: 'Wallet',
};

const note: CSSProperties = { fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5 };
const input: CSSProperties = {
  width: '100%', height: 32, padding: '0 10px', borderRadius: 'var(--r-xs)',
  border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)',
  fontSize: 'var(--t-body)',
};

export function SendRightCard({ gln }: { gln: string }) {
  const [rows, setRows] = useState<BoChannelAllowance[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** Le canal dont on est en train de saisir le motif — un seul à la fois. */
  const [pending, setPending] = useState<BoChannelAllowance['channel'] | null>(null);
  const [reason, setReason] = useState('');

  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : changer de magasin pendant la lecture afficherait les droits d'envoi de
  //    l'ANCIEN ; et la relecture qui suit une suspension croise celle de l'ouverture — revenue la dernière,
  //    l'ancienne montrerait le canal encore ouvert alors qu'il vient d'être suspendu (ou l'inverse).
  const latest = useLatest();
  const load = useCallback(() => {
    const isLatest = latest();
    return api.boChannelAllowances(gln)
      .then((r) => {
        if (!isLatest()) return;
        setRows(r.allowances ?? []);
        setErr(r.ok ? null : (r.error ?? 'Droits d’envoi illisibles.'));
      })
      .catch((e) => { if (!isLatest()) return; setRows([]); setErr((e as Error).message); });
  }, [gln, latest]);
  useEffect(() => { setRows(null); setPending(null); void load(); }, [load]);

  const apply = async (channel: BoChannelAllowance['channel'], allowance: number | null, why: string | null) => {
    setBusy(channel);
    try {
      const r = await api.setBoChannelAllowance(gln, { channel, allowance, ...(why ? { reason: why } : {}) });
      // §23.0 : le motif RÉEL du serveur, jamais une phrase rassurante.
      if (!r.ok) throw new Error(r.error ?? 'Le back-office n’a pas répondu.');
      showToast(allowance === 0 ? 'Canal suspendu.' : 'Canal rendu.');
      setPending(null); setReason('');
      await load();
    } catch (e) {
      await alert({ title: 'Changement impossible', message: (e as Error).message, tone: 'err' });
    } finally { setBusy(null); }
  };

  const restore = async (l: BoChannelAllowance) => {
    if (!await confirm({
      title: `Rendre le canal « ${CHANNEL_LABELS[l.channel]} » ?`,
      message: 'Le magasin pourra de nouveau lancer des campagnes sur ce canal. La trace de la suspension reste au journal.',
      confirmLabel: 'Rendre le droit',
      tone: 'info',
    })) return;
    await apply(l.channel, null, null);
  };

  return (
    <div className="dcard" style={{ padding: '16px 18px' }}>
      <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>Droit d’envoyer</div>
      <div style={{ ...note, marginTop: 4 }}>
        Ce que ce magasin a le droit d’envoyer. Une suspension refuse le lancement d’une campagne sur
        ce canal, et le motif s’affiche au commerçant.
      </div>

      {err && <div style={{ fontSize: 'var(--t-meta)', color: 'var(--da-fg)', marginTop: 8, lineHeight: 1.5, overflowWrap: 'anywhere' }}>{err}</div>}
      {!rows && !err && <div style={{ ...note, marginTop: 8 }}>Lecture des droits…</div>}

      {rows?.map((l) => {
        const blocked = l.allowance === 0;
        return (
          <div key={l.channel} style={{ padding: '9px 0', borderBottom: '1px solid var(--ink100)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, flex: 1, minWidth: 120 }}>{CHANNEL_LABELS[l.channel]}</span>
              <span className="mono" style={{
                fontSize: 'var(--t-micro)', padding: '2px 8px', borderRadius: 999,
                background: blocked ? 'var(--da-bg)' : 'var(--ok-bg)',
                color: blocked ? 'var(--da-fg)' : 'var(--ok-fg)',
              }}>{blocked ? 'suspendu' : 'autorisé'}</span>
              <button className="ghost btn-ghost" style={{ width: 'auto', padding: '4px 11px' }}
                onClick={() => {
                  if (blocked) { void restore(l); return; }
                  setPending(pending === l.channel ? null : l.channel); setReason('');
                }}
                disabled={busy !== null}>
                {blocked ? 'Rendre' : pending === l.channel ? 'Annuler' : 'Suspendre'}
              </button>
            </div>

            {blocked && l.reason && (
              <div style={{ ...note, marginTop: 4, color: 'var(--da-fg)' }}>« {l.reason} »</div>
            )}
            {/* ⚠️ QUI et QUAND s'affichent : couper l'envoi d'un client est un geste qui se conteste,
                dans les deux sens — et une trace qu'il faut aller chercher au journal ne sert pas ici. */}
            {l.updatedBy && (
              <div style={{ ...note, marginTop: 2, fontSize: 'var(--t-micro)' }}>
                {blocked ? 'Suspendu' : 'Rendu'} par {l.updatedBy}
                {l.updatedAt ? ` le ${new Date(l.updatedAt).toLocaleDateString()}` : ''}
              </div>
            )}

            {pending === l.channel && (
              <div style={{ marginTop: 8 }}>
                <div style={{ ...note, marginBottom: 4 }}>
                  Motif — il sera <strong>affiché au commerçant</strong> à la place de son bouton d’envoi.
                </div>
                <input style={input} value={reason} autoFocus aria-label={`Motif de la suspension — ${CHANNEL_LABELS[l.channel]}`}
                  placeholder="Ex. : facture du mois de septembre impayée"
                  onChange={(e) => setReason(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && reason.trim()) void apply(l.channel, 0, reason.trim()); }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {/* ⚠️ LE BOUTON RESTE INERTE SANS MOTIF plutôt que de le demander après coup : le
                      serveur refuse de toute façon, et un aller-retour pour l'apprendre n'apprend rien. */}
                  <button className="ghost btn-ghost" style={{ width: 'auto', padding: '5px 12px' }}
                    onClick={() => void apply(l.channel, 0, reason.trim())}
                    disabled={busy !== null || !reason.trim()}>
                    Suspendre ce canal
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ⚠️ CE QUI N'EXISTE PAS SE DIT, plutôt que de laisser croire à un oubli d'écran (§23.0). */}
      <div style={{ ...note, marginTop: 10, fontSize: 'var(--t-micro)' }}>
        Un <strong>plafond chiffré</strong> (« 500 messages ») n’est pas proposé : sa fenêtre de temps
        — par mois, en enveloppe unique ou glissante — n’est pas tranchée, et rien ne l’appliquerait
        encore. Suspendre agit sur <strong>notre</strong> compteur seulement : le fournisseur continue
        de facturer ce qui part.
      </div>
    </div>
  );
}
